import { useEffect, useState } from 'react'
import type { Vcard } from '../types'

/**
 * Eine Öffnungszeiten-Zeile im Formzustand: eine Zeitspanne (Öffnen/Schließen
 * als "HH:MM") für **mehrere** Wochentage. Beim Speichern wird pro Tag ein
 * Backend-Eintrag erzeugt (Kontrakt `teams.openingHours` bleibt „ein Tag pro
 * Eintrag"); beim Laden werden Tage mit identischer Zeit zusammengefasst.
 */
export interface VcardHourRow {
  days: number[] // 1 (Mo) – 7 (So), aufsteigend
  open: string // "HH:MM" für <input type="time">
  close: string
}

/**
 * Flacher Formzustand des vCard-Editors. Verschachtelte Backend-Felder
 * (teams/address/openingHours) werden für die Eingabe flachgezogen und beim
 * Speichern über `buildPayload` wieder in die Backend-Form zusammengesetzt.
 */
export interface VcardFormState {
  firstName: string
  lastName: string
  role: string
  profileImageUrl: string
  email: string
  phone: string
  mobile: string
  whatsapp: string
  facebook: string
  linkedin: string
  website: string
  googleBusinessMessage: string
  teamName: string
  teamLogo: string
  street: string
  houseNumber: string
  zip: string
  city: string
  /** Öffnungszeiten, ein Eintrag pro Tag (Backend: `teams.openingHours`). */
  hours: VcardHourRow[]
  active: boolean
}

/** String-Felder des Formzustands (alles außer `active`/`hours`). */
export type VcardFormField = Exclude<keyof VcardFormState, 'active' | 'hours'>

/**
 * Optionale Kontakt-/Social-Felder, die per Leerwert entfernt werden
 * („leer lassen entfernt das Feld", Spec §10).
 */
const REMOVABLE_FIELDS: VcardFormField[] = [
  'email',
  'phone',
  'mobile',
  'whatsapp',
  'facebook',
  'linkedin',
  'website',
  'googleBusinessMessage',
]

/** Robuste String-Konvertierung — Backend-Werte sind nicht immer Strings. */
function str(value: unknown): string {
  return value == null ? '' : String(value)
}

/** Backend-Zeit ("0900") → Eingabefeld-Zeit ("09:00"). */
function toInputTime(raw: string): string {
  const digits = raw.replace(/\D/g, '')
  if (!digits) return ''
  const p = digits.padStart(4, '0').slice(-4)
  return `${p.slice(0, 2)}:${p.slice(2)}`
}

/** Eingabefeld-Zeit ("09:00") → Backend-Zeit ("0900"). */
function toApiTime(input: string): string {
  return input.replace(/\D/g, '')
}

/**
 * Gruppiert Backend-Öffnungszeiten (ein Eintrag pro Tag) zu Formzeilen: Tage
 * mit identischer Öffnen/Schließen-Zeit landen in einer Zeile (mehrere Tage).
 * Reihenfolge der Zeilen = erstes Auftreten der Zeitspanne.
 */
function groupHours(
  openingHours: NonNullable<Vcard['teams']>['openingHours'],
): VcardHourRow[] {
  const byTime = new Map<string, VcardHourRow>()
  const order: string[] = []
  for (const oh of openingHours ?? []) {
    const open = toInputTime(str(oh.open?.time))
    const close = toInputTime(str(oh.close?.time))
    const day = oh.open?.day ?? 1
    const key = `${open}|${close}`
    let row = byTime.get(key)
    if (!row) {
      row = { days: [], open, close }
      byTime.set(key, row)
      order.push(key)
    }
    if (!row.days.includes(day)) row.days.push(day)
  }
  return order.map((key) => {
    const row = byTime.get(key) as VcardHourRow
    return { ...row, days: [...row.days].sort((a, b) => a - b) }
  })
}

function toFormState(vc: Vcard): VcardFormState {
  const addr = vc.teams?.address
  return {
    firstName: str(vc.firstName),
    lastName: str(vc.lastName),
    role: str(vc.role),
    profileImageUrl: str(vc.profileImageUrl),
    email: str(vc.email),
    phone: str(vc.phone),
    mobile: str(vc.mobile),
    whatsapp: str(vc.whatsapp),
    facebook: str(vc.facebook),
    linkedin: str(vc.linkedin),
    website: str(vc.website),
    googleBusinessMessage: str(vc.googleBusinessMessage),
    teamName: str(vc.teams?.name),
    teamLogo: str(vc.teams?.logoUrl),
    street: str(addr?.street),
    houseNumber: str(addr?.houseNumber),
    zip: str(addr?.zip),
    city: str(addr?.city),
    hours: groupHours(vc.teams?.openingHours),
    active: vc.active === true,
  }
}

export interface UseVcardForm {
  values: VcardFormState
  setField: (field: VcardFormField, value: string) => void
  setActive: (active: boolean) => void
  /** Aktualisiert Öffnen/Schließen einer Öffnungszeiten-Zeile. */
  setHour: (index: number, patch: Partial<Pick<VcardHourRow, 'open' | 'close'>>) => void
  /** Schaltet einen Wochentag in einer Zeile an/aus (Mehrfachauswahl). */
  toggleDay: (index: number, day: number) => void
  /** Fügt eine neue Öffnungszeiten-Zeile hinzu (default Mo, leere Zeiten). */
  addHour: () => void
  /** Entfernt die Öffnungszeiten-Zeile am gegebenen Index. */
  removeHour: (index: number) => void
  /** Setzt den Formzustand auf die Ausgangs-vCard zurück (Verwerfen). */
  reset: () => void
  /** Vollständige vCard aus dem Formzustand — für die Live-Vorschau. */
  preview: Vcard
  /** PUT-Replace-Payload (getrimmt, in Backend-Form) für das Speichern. */
  buildPayload: () => Vcard
  /** Namen der aktuell geleerten optionalen Felder (für den Speichern-Toast). */
  clearedFields: () => VcardFormField[]
}

/**
 * Hält den editierbaren Formzustand einer vCard, liefert die abgeleitete
 * Live-Vorschau und den Speicher-Payload. Bei Wechsel der vCard (`urlKey`)
 * werden die Werte neu übernommen.
 *
 * @param initial Die zu bearbeitende vCard
 */
export function useVcardForm(initial: Vcard): UseVcardForm {
  const [values, setValues] = useState<VcardFormState>(() => toFormState(initial))

  useEffect(() => {
    setValues(toFormState(initial))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial.urlKey])

  const setField = (field: VcardFormField, value: string): void => {
    setValues((prev) => ({ ...prev, [field]: value }))
  }

  const setActive = (active: boolean): void => {
    setValues((prev) => ({ ...prev, active }))
  }

  const setHour = (
    index: number,
    patch: Partial<Pick<VcardHourRow, 'open' | 'close'>>,
  ): void => {
    setValues((prev) => ({
      ...prev,
      hours: prev.hours.map((h, i) => (i === index ? { ...h, ...patch } : h)),
    }))
  }

  const toggleDay = (index: number, day: number): void => {
    setValues((prev) => ({
      ...prev,
      hours: prev.hours.map((h, i) =>
        i === index
          ? {
              ...h,
              days: h.days.includes(day)
                ? h.days.filter((d) => d !== day)
                : [...h.days, day].sort((a, b) => a - b),
            }
          : h,
      ),
    }))
  }

  const addHour = (): void => {
    setValues((prev) => ({ ...prev, hours: [...prev.hours, { days: [1], open: '', close: '' }] }))
  }

  const removeHour = (index: number): void => {
    setValues((prev) => ({ ...prev, hours: prev.hours.filter((_, i) => i !== index) }))
  }

  const reset = (): void => {
    setValues(toFormState(initial))
  }

  const buildPayload = (): Vcard => {
    const t = (s: string): string => str(s).trim()
    return {
      urlKey: initial.urlKey,
      brandKey: str(initial.brandKey),
      externalId: initial.externalId,
      firstName: t(values.firstName),
      lastName: t(values.lastName),
      role: t(values.role),
      profileImageUrl: t(values.profileImageUrl),
      email: t(values.email),
      phone: t(values.phone),
      mobile: t(values.mobile),
      whatsapp: t(values.whatsapp),
      facebook: t(values.facebook),
      linkedin: t(values.linkedin),
      website: t(values.website),
      googleBusinessMessage: t(values.googleBusinessMessage),
      teams: {
        id: initial.teams?.id,
        name: t(values.teamName),
        logoUrl: t(values.teamLogo),
        address: {
          street: t(values.street),
          houseNumber: t(values.houseNumber),
          zip: t(values.zip),
          city: t(values.city),
        },
        // Mehrtage-Zeilen → ein Backend-Eintrag pro Tag, nach Tag sortiert.
        openingHours: values.hours
          .flatMap((h) =>
            h.days.map((day) => ({
              open: { day, time: toApiTime(h.open) },
              close: { day, time: toApiTime(h.close) },
            })),
          )
          .sort((a, b) => a.open.day - b.open.day),
      },
      active: values.active,
    }
  }

  const clearedFields = (): VcardFormField[] =>
    REMOVABLE_FIELDS.filter((field) => values[field].trim() === '')

  const preview: Vcard = buildPayload()

  return {
    values,
    setField,
    setActive,
    setHour,
    toggleDay,
    addHour,
    removeHour,
    reset,
    preview,
    buildPayload,
    clearedFields,
  }
}
